import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { PROFILE } from "../data/content";

const ORBS = [
  { size: 520, x: 18, y: 22, color: "rgba(209,138,88,0.45)", b: 80, d: 18, dir: 1 },
  { size: 380, x: 72, y: 64, color: "rgba(197,162,155,0.45)", b: 90, d: 22, dir: -1 },
  { size: 260, x: 86, y: 18, color: "rgba(242,235,229,0.20)", b: 60, d: 14, dir: 1 },
  { size: 220, x: 30, y: 78, color: "rgba(209,138,88,0.30)", b: 70, d: 16, dir: -1 },
];

export default function Hero() {
  const [pointer, setPointer] = useState({ x: 0, y: 0 });
  const wrapRef = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      const w = window.innerWidth, h = window.innerHeight;
      setPointer({ x: (e.clientX / w) * 2 - 1, y: ((e.clientY / h) * 2 - 1) });
    };
    const onTilt = (e) => {
      if (e.gamma == null || e.beta == null) return;
      setPointer({
        x: Math.max(-1, Math.min(1, e.gamma / 30)),
        y: Math.max(-1, Math.min(1, e.beta / 60)),
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("deviceorientation", onTilt);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("deviceorientation", onTilt);
    };
  }, []);

  return (
    <section
      id="home"
      ref={wrapRef}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0B0E14] text-[#F2EBE5]"
      data-testid="hero-section"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_luxury-3d-style/artifacts/t6bb67vv_FullSizeRender.jpeg"
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0E14] via-[#0B0E14]/85 to-[#1a0f0a]" />
      </div>

      {/* Floating orbs (parallax) */}
      <div className="absolute inset-0 pointer-events-none">
        {ORBS.map((o, i) => (
          <motion.div
            key={i}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{
              scale: 1,
              opacity: 1,
              x: pointer.x * o.d * o.dir,
              y: pointer.y * o.d * o.dir,
            }}
            transition={{
              opacity: { duration: 1.4, delay: i * 0.12 },
              scale: { duration: 1.4, delay: i * 0.12 },
              x: { type: "spring", stiffness: 30, damping: 20 },
              y: { type: "spring", stiffness: 30, damping: 20 },
            }}
            className="absolute rounded-full mix-blend-screen"
            style={{
              width: o.size,
              height: o.size,
              left: `${o.x}%`,
              top: `${o.y}%`,
              background: `radial-gradient(circle at 30% 30%, ${o.color}, transparent 65%)`,
              filter: `blur(${o.b}px)`,
              transform: `translate(-50%, -50%)`,
            }}
          />
        ))}
      </div>

      <div className="absolute inset-0 grain-overlay pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0B0E14] pointer-events-none" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-10 pt-32 md:pt-40 pb-24 min-h-[100svh] flex flex-col justify-between">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="flex items-center gap-3 text-[10px] md:text-xs tracking-overline uppercase text-[#C5A29B]"
        >
          <span className="h-px w-10 bg-[#D18A58]" />
          Portfolio · Index {String(new Date().getFullYear()).slice(-2)}
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-9 max-w-5xl">
          <h1 className="font-display text-[14vw] sm:text-[12vw] md:text-[9vw] lg:text-[8.5vw] leading-[0.92] tracking-[-0.02em] text-[#F2EBE5]">
            <span className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.05, ease: [0.7, 0, 0.2, 1], delay: 0.15 }}
                className="block"
              >
                {PROFILE.firstName}
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.05, ease: [0.7, 0, 0.2, 1], delay: 0.3 }}
                className="block italic text-[#D18A58]"
              >
                {PROFILE.lastName}<span className="text-[#F2EBE5]">.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-6 md:mt-8 max-w-xl text-base md:text-lg text-[#C5A29B] leading-relaxed"
          >
            {PROFILE.title} — shaping visual narratives for luxury, beauty and editorial.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-8 md:mt-10 flex flex-wrap items-center gap-3"
          >
            <a
              href="#portfolio"
              data-testid="hero-view-work-btn"
              className="group inline-flex items-center gap-3 rounded-full bg-[#D18A58] px-7 py-3.5 text-sm uppercase tracking-overline text-[#0B0E14] font-medium hover:bg-[#F2EBE5] transition-colors"
            >
              View The Work
              <span className="inline-block transition-transform group-hover:translate-x-1">↗</span>
            </a>
            <a
              href="#contact"
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-3 rounded-full border border-[#F2EBE5]/30 px-7 py-3.5 text-sm uppercase tracking-overline text-[#F2EBE5] hover:bg-[#F2EBE5]/10 transition-colors"
            >
              Get In Touch
            </a>
          </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.55, ease: [0.7, 0, 0.2, 1] }}
            className="lg:col-span-3 relative hidden lg:block"
            data-testid="hero-portrait"
          >
            <div className="relative aspect-[3/4] overflow-hidden rounded-sm">
              <div className="absolute -inset-3 bg-[#D18A58]/30 rotate-3" />
              <img
                src={PROFILE.about_image}
                alt={PROFILE.name}
                className="relative z-10 h-full w-full object-cover object-top grayscale-[0.1] saturate-[1.05]"
              />
              <div className="absolute z-10 inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0B0E14]/70 to-transparent" />
              <div className="absolute z-20 bottom-3 left-3 right-3 flex justify-between items-end text-[10px] uppercase tracking-overline text-[#F2EBE5]">
                <span>Portrait</span>
                <span>' 25</span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-16 flex items-end justify-between"
        >
          <div className="flex items-center gap-3 text-[10px] tracking-overline uppercase text-[#8A8D98]">
            <ArrowDown className="h-4 w-4 animate-bounce text-[#D18A58]" />
            Scroll
          </div>
          <div className="hidden md:block text-right text-[10px] tracking-overline uppercase text-[#8A8D98] max-w-[18ch]">
            Vadodara · Mumbai · Dubai
          </div>
        </motion.div>
      </div>

      {/* Marquee */}
      <div className="relative z-10 border-y border-[#F2EBE5]/10 overflow-hidden bg-[#0B0E14]/40 backdrop-blur">
        <div className="marquee-track flex whitespace-nowrap py-4">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-12 px-6 font-display italic text-2xl md:text-3xl text-[#C5A29B]">
              {["Creative Direction", "Visual Storytelling", "Luxury Branding", "Editorial", "Styling", "Exhibition Design"].map((w, i) => (
                <span key={i} className="flex items-center gap-12">
                  <span>{w}</span>
                  <span className="text-[#D18A58]">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
